import React, { createContext, useContext, useState, ReactNode } from "react";
import { useMemo } from "react";

interface TourGuideContextType {
  activeTourModule: string | null;
  highlightedItemKey: string | null;
  setHighlightedItemKey: (key: string | null) => void;
  setActiveTourModule: (module: string | null) => void;
  currentTourStep: number;
  setCurrentTourStep: (step: number) => void;
  hoverTooltipsEnabled: boolean;
  setHoverTooltipsEnabled: (enabled: boolean) => void;
  hoveredStepId: string | null;
  setHoveredStepId: (stepId: string | null) => void;
  disabledHoverSteps: Set<string>; // Set of step IDs that should not show hover tooltips
  setDisabledHoverSteps: (
    steps: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
}

const TourGuideContext = createContext<TourGuideContextType | undefined>(
  undefined
);

export const TourGuideProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [activeTourModule, setActiveTourModule] = useState<string | null>(null);
  const [highlightedItemKey, setHighlightedItemKey] = useState<string | null>(
    null
  );
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [hoverTooltipsEnabled, setHoverTooltipsEnabled] =
    useState<boolean>(true);
  const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);
  // To disable individual hover tooltips, add step IDs to this Set
  // Example: setDisabledHoverSteps(new Set(["pill-toggle", "textarea"]))
  // To disable all: setHoverTooltipsEnabled(false)
  // Currently disabled:
  // - Legal chatbot: sidebar-legal, textarea, suggested-questions
  // - Project chatbot: sidebar-project (step 1)
  // - Permit review: sidebar-permit (step 1), permit-upload-area (step 3), permit-data-table (step 4), permit-document-actions (step 5)
  // - Material comparison: sidebar-material (step 1), files-list (step 4)
  // - Drawing analysis: drawing-textarea (step 1)
  // - Multi-document: multi-document-textarea (step 1)
  // - KCS chatbot: kcs-textarea (step 1)
  const [disabledHoverSteps, setDisabledHoverSteps] = useState<Set<string>>(
    new Set([
      "sidebar-legal",
      "textarea",
      "suggested-questions",
      "sidebar-project",
      "sidebar-permit",
      "permit-upload-area",
      "permit-data-table",
      "permit-document-actions",
      "sidebar-material",
      "files-list",
      "drawing-textarea",
      "multi-document-textarea",
      "kcs-textarea",
    ])
  );

  const contextValue = useMemo(
    () => ({
      activeTourModule,
      highlightedItemKey,
      setHighlightedItemKey,
      setActiveTourModule,
      currentTourStep,
      setCurrentTourStep,
      hoverTooltipsEnabled,
      setHoverTooltipsEnabled,
      hoveredStepId,
      setHoveredStepId,
      disabledHoverSteps,
      setDisabledHoverSteps,
    }),
    [
      activeTourModule,
      highlightedItemKey,
      currentTourStep,
      hoverTooltipsEnabled,
      hoveredStepId,
      disabledHoverSteps,
    ]
  );

  return (
    <TourGuideContext.Provider value={contextValue}>
      {children}
    </TourGuideContext.Provider>
  );
};

export const useTourGuideContext = () => {
  const context = useContext(TourGuideContext);
  if (!context) {
    throw new Error(
      "useTourGuideContext must be used within TourGuideProvider"
    );
  }
  return context;
};
